import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Cog6ToothIcon,
  KeyIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  ComputerDesktopIcon,
  GlobeAltIcon,
  BellIcon,
  EyeIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  FingerPrintIcon,
  UserCircleIcon,
  CreditCardIcon,
  ArrowLeftOnRectangleIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/solid';
import Header from '../../components/Header';
import Footer from '../../components/Footer';

const AccountSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general');
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Placeholder handlers for modal functionality
  const handlePasswordChange = () => {
    // TODO: Implement password change modal
    alert('Password change functionality will be implemented');
  };

  const handle2FASetup = () => {
    // TODO: Implement 2FA setup modal
    alert('2FA setup functionality will be implemented');
  };

  const handleDeleteAccount = () => {
    // TODO: Implement delete account confirmation modal
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      alert('Account deletion functionality will be implemented');
    }
  };

  useEffect(() => {
    // Simulate fetching user settings
    const fetchSettings = () => {
      const mockSettings = {
        // General Settings
        general: {
          firstName: 'Dr. Sarah',
          lastName: 'Johnson',
          email: 'sarah.johnson@apollohealthcare.com',
          phone: '+1 (555) 123-4567',
          title: 'Senior Cardiologist',
          department: 'Cardiology',
          employeeId: 'AHC-001234',
          timezone: 'America/New_York',
          language: 'en-US',
          dateFormat: 'MM/DD/YYYY',
          timeFormat: '12h'
        },

        // Security Settings
        security: {
          lastPasswordChange: '2024-05-15',
          twoFactorEnabled: true,
          loginNotifications: true,
          sessionTimeout: 30,
          trustedDevices: [
            {
              id: 1,
              name: 'MacBook Pro',
              type: 'desktop',
              browser: 'Chrome',
              location: 'New York, NY',
              lastAccess: '2024-07-23T14:30:00Z',
              current: true
            },
            {
              id: 2,
              name: 'iPhone 14',
              type: 'mobile',
              browser: 'Safari',
              location: 'New York, NY',
              lastAccess: '2024-07-23T12:15:00Z',
              current: false
            },
            {
              id: 3,
              name: 'iPad Pro',
              type: 'tablet',
              browser: 'Safari',
              location: 'New York, NY',
              lastAccess: '2024-07-22T18:45:00Z',
              current: false
            }
          ],
          loginHistory: [
            {
              id: 1,
              device: 'MacBook Pro',
              location: 'New York, NY',
              timestamp: '2024-07-23T08:30:00Z',
              success: true,
              ip: '192.168.1.100'
            },
            {
              id: 2,
              device: 'iPhone 14',
              location: 'New York, NY',
              timestamp: '2024-07-22T19:15:00Z',
              success: true,
              ip: '192.168.1.105'
            },
            {
              id: 3,
              device: 'Unknown Device',
              location: 'Los Angeles, CA',
              timestamp: '2024-07-21T23:45:00Z',
              success: false,
              ip: '203.0.113.195'
            }
          ]
        },

        // Notification Settings
        notifications: {
          email: {
            systemUpdates: true,
            securityAlerts: true,
            appointmentReminders: true,
            taskAssignments: true,
            messageNotifications: false,
            weeklyReports: true,
            maintenanceAlerts: true
          },
          push: {
            appointmentReminders: true,
            emergencyAlerts: true,
            taskDeadlines: true,
            messageNotifications: true,
            systemNotifications: false
          },
          sms: {
            securityAlerts: true,
            emergencyAlerts: true,
            appointmentReminders: false,
            systemDowntime: true
          }
        },

        // Privacy Settings
        privacy: {
          profileVisibility: 'team',
          showOnlineStatus: true,
          shareContactInfo: true,
          allowDirectMessages: true,
          shareActivityStatus: false,
          dataCollection: true,
          analyticsOptIn: false,
          marketingEmails: false
        },

        // Application Settings
        application: {
          theme: 'light',
          compactMode: false,
          showTooltips: true,
          autoSave: true,
          defaultDashboard: 'overview',
          itemsPerPage: 25,
          enableKeyboardShortcuts: true,
          showBetaFeatures: false
        },

        // Integration Settings
        integrations: {
          calendar: {
            provider: 'google',
            syncEnabled: true,
            lastSync: '2024-07-23T14:00:00Z'
          },
          email: {
            provider: 'outlook',
            syncEnabled: true,
            lastSync: '2024-07-23T13:45:00Z'
          },
          storage: {
            provider: 'onedrive',
            syncEnabled: false,
            lastSync: null
          }
        },

        // Billing & Subscription
        billing: {
          plan: 'Professional',
          status: 'active',
          renewalDate: '2024-12-31',
          paymentMethod: {
            type: 'credit_card',
            last4: '4242',
            brand: 'Visa',
            expiryMonth: 12,
            expiryYear: 2026
          },
          billingHistory: [
            {
              id: 1,
              date: '2024-06-01',
              amount: 299.99,
              description: 'Professional Plan - Monthly',
              status: 'paid'
            },
            {
              id: 2,
              date: '2024-05-01',
              amount: 299.99,
              description: 'Professional Plan - Monthly',
              status: 'paid'
            }
          ]
        }
      };

      setSettings(mockSettings);
      setLoading(false);
    };

    setTimeout(fetchSettings, 500);
  }, []);

  const handleSave = async (section, field, value) => {
    setSaving(true);
    
    // Simulate API call
    setTimeout(() => {
      setSettings(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
      setEditingField(null);
      setSaving(false);
    }, 1000);
  };

  const handleEdit = (field, currentValue) => {
    setEditingField(field);
    setEditValue(currentValue);
  };

  const handleCancel = () => {
    setEditingField(null);
    setEditValue('');
  };

  const toggleNotification = (category, type) => {
    setSettings(prev => ({
      ...prev,
      notifications: {
        ...prev.notifications,
        [category]: {
          ...prev.notifications[category],
          [type]: !prev.notifications[category][type]
        }
      }
    }));
  };

  const togglePrivacySetting = (setting) => {
    setSettings(prev => ({
      ...prev,
      privacy: {
        ...prev.privacy,
        [setting]: !prev.privacy[setting]
      }
    }));
  };

  const toggleApplicationSetting = (setting) => {
    setSettings(prev => ({
      ...prev,
      application: {
        ...prev.application,
        [setting]: !prev.application[setting]
      }
    }));
  };

  const removeDevice = (deviceId) => {
    setSettings(prev => ({
      ...prev,
      security: {
        ...prev.security,
        trustedDevices: prev.security.trustedDevices.filter(device => device.id !== deviceId)
      }
    }));
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'mobile':
        return <DevicePhoneMobileIcon className="h-5 w-5" />;
      case 'tablet':
        return <DevicePhoneMobileIcon className="h-5 w-5" />;
      default:
        return <ComputerDesktopIcon className="h-5 w-5" />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const sections = [
    { id: 'general', name: 'General', icon: UserCircleIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'privacy', name: 'Privacy', icon: EyeIcon },
    { id: 'application', name: 'Application', icon: Cog6ToothIcon },
    { id: 'integrations', name: 'Integrations', icon: GlobeAltIcon },
    { id: 'billing', name: 'Billing', icon: CreditCardIcon }
  ];

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      {/* Custom Header for Account Settings */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4 sm:py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0">
                <Cog6ToothIcon className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600 sm:mr-3" />
                <div>
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Account Settings</h1>
                  <p className="mt-1 sm:mt-2 text-sm text-gray-700">
                    Manage your account preferences and security settings
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Link
                  to="/profile"
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  <UserCircleIcon className="h-4 w-4 mr-2" />
                  View Profile
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Sidebar */}
          <div className="w-full lg:w-64 flex-shrink-0">
            <nav className="space-y-1 bg-white lg:bg-transparent rounded-lg lg:rounded-none p-4 lg:p-0 shadow lg:shadow-none">
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`${
                    activeSection === section.id
                      ? 'bg-blue-50 border-blue-500 text-blue-700'
                      : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group border-l-4 px-3 py-2 flex items-center text-sm font-medium w-full rounded-r-md lg:rounded-r-none`}
                >
                  <section.icon className="text-gray-400 group-hover:text-gray-500 mr-3 flex-shrink-0 h-4 w-4 sm:h-5 sm:w-5" />
                  {section.name}
                </button>
              ))}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            {/* General Settings */}
            {activeSection === 'general' && (
              <div className="space-y-6">
                <div className="bg-white shadow rounded-lg">
                  <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
                    <h3 className="text-base sm:text-lg font-medium text-gray-900">General Information</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Update your personal and professional information
                    </p>
                  </div>
                  <div className="px-4 sm:px-6 py-4 space-y-6">
                    {/* Name Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">First Name</label>
                        {editingField === 'firstName' ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="flex-1 border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button
                              onClick={() => handleSave('general', 'firstName', editValue)}
                              disabled={saving}
                              className="p-2 text-green-600 hover:text-green-800"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={handleCancel}
                              className="p-2 text-gray-400 hover:text-gray-600"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-900">{settings.general.firstName}</span>
                            <button
                              onClick={() => handleEdit('firstName', settings.general.firstName)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
                        {editingField === 'lastName' ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="flex-1 border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button
                              onClick={() => handleSave('general', 'lastName', editValue)}
                              disabled={saving}
                              className="p-2 text-green-600 hover:text-green-800"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={handleCancel}
                              className="p-2 text-gray-400 hover:text-gray-600"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-900">{settings.general.lastName}</span>
                            <button
                              onClick={() => handleEdit('lastName', settings.general.lastName)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Contact Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                        <div className="flex items-center justify-between">
                          <span className="text-gray-900">{settings.general.email}</span>
                          <span className="text-xs text-gray-500">Contact IT to change</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                        {editingField === 'phone' ? (
                          <div className="flex items-center space-x-2">
                            <input
                              type="tel"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="flex-1 border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            />
                            <button
                              onClick={() => handleSave('general', 'phone', editValue)}
                              disabled={saving}
                              className="p-2 text-green-600 hover:text-green-800"
                            >
                              <CheckIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={handleCancel}
                              className="p-2 text-gray-400 hover:text-gray-600"
                            >
                              <XMarkIcon className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <span className="text-gray-900">{settings.general.phone}</span>
                            <button
                              onClick={() => handleEdit('phone', settings.general.phone)}
                              className="p-1 text-gray-400 hover:text-gray-600"
                            >
                              <PencilIcon className="h-4 w-4" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Localization */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                        <select
                          value={settings.general.timezone}
                          onChange={(e) => handleSave('general', 'timezone', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="America/New_York">Eastern Time</option>
                          <option value="America/Chicago">Central Time</option>
                          <option value="America/Denver">Mountain Time</option>
                          <option value="America/Los_Angeles">Pacific Time</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
                        <select
                          value={settings.general.dateFormat}
                          onChange={(e) => handleSave('general', 'dateFormat', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                          <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                          <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Time Format</label>
                        <select
                          value={settings.general.timeFormat}
                          onChange={(e) => handleSave('general', 'timeFormat', e.target.value)}
                          className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="12h">12 Hour</option>
                          <option value="24h">24 Hour</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Security Settings */}
            {activeSection === 'security' && (
              <div className="space-y-6">
                {/* Password & Authentication */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Password & Authentication</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Manage your password and two-factor authentication
                    </p>
                  </div>
                  <div className="px-6 py-4 space-y-4">
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Password</h4>
                        <p className="text-sm text-gray-500">
                          Last changed {new Date(settings.security.lastPasswordChange).toLocaleDateString()}
                        </p>
                      </div>
                      <button
                        onClick={handlePasswordChange}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <KeyIcon className="h-4 w-4 mr-2" />
                        Change Password
                      </button>
                    </div>

                    <div className="flex items-center justify-between py-3 border-t">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Two-Factor Authentication</h4>
                        <p className="text-sm text-gray-500">
                          {settings.security.twoFactorEnabled ? 'Enabled' : 'Disabled'} - Add an extra layer of security
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {settings.security.twoFactorEnabled && (
                          <CheckCircleIcon className="h-5 w-5 text-green-500" />
                        )}
                        <button
                          onClick={handle2FASetup}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <FingerPrintIcon className="h-4 w-4 mr-2" />
                          {settings.security.twoFactorEnabled ? 'Manage' : 'Enable'} 2FA
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-between py-3 border-t">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Login Notifications</h4>
                        <p className="text-sm text-gray-500">
                          Get notified when someone signs into your account
                        </p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={settings.security.loginNotifications}
                          onChange={() => setSettings(prev => ({
                            ...prev,
                            security: {
                              ...prev.security,
                              loginNotifications: !prev.security.loginNotifications
                            }
                          }))}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Trusted Devices */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Trusted Devices</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Devices that have been used to access your account
                    </p>
                  </div>
                  <div className="px-6 py-4">
                    <div className="space-y-4">
                      {settings.security.trustedDevices.map((device) => (
                        <div key={device.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div className="flex items-center">
                            <div className="p-2 bg-gray-100 rounded-lg mr-4">
                              {getDeviceIcon(device.type)}
                            </div>
                            <div>
                              <div className="flex items-center">
                                <h4 className="text-sm font-medium text-gray-900">{device.name}</h4>
                                {device.current && (
                                  <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Current
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">
                                {device.browser} • {device.location}
                              </p>
                              <p className="text-xs text-gray-400">
                                Last access: {new Date(device.lastAccess).toLocaleString()}
                              </p>
                            </div>
                          </div>
                          {!device.current && (
                            <button
                              onClick={() => removeDevice(device.id)}
                              className="p-2 text-red-400 hover:text-red-600"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Login History */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Recent Login Activity</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Review recent login attempts to your account
                    </p>
                  </div>
                  <div className="px-6 py-4">
                    <div className="space-y-3">
                      {settings.security.loginHistory.map((login) => (
                        <div key={login.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0">
                          <div className="flex items-center">
                            {login.success ? (
                              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-3" />
                            ) : (
                              <XCircleIcon className="h-5 w-5 text-red-500 mr-3" />
                            )}
                            <div>
                              <p className="text-sm text-gray-900">
                                {login.success ? 'Successful login' : 'Failed login attempt'}
                              </p>
                              <p className="text-xs text-gray-500">
                                {login.device} • {login.location} • {login.ip}
                              </p>
                            </div>
                          </div>
                          <span className="text-sm text-gray-500">
                            {new Date(login.timestamp).toLocaleString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Notification Settings */}
            {activeSection === 'notifications' && (
              <div className="space-y-6">
                {/* Email Notifications */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Email Notifications</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Choose what email notifications you'd like to receive
                    </p>
                  </div>
                  <div className="px-6 py-4 space-y-4">
                    {Object.entries(settings.notifications.email).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </h4>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={() => toggleNotification('email', key)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Push Notifications */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Push Notifications</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Configure browser and mobile push notifications
                    </p>
                  </div>
                  <div className="px-6 py-4 space-y-4">
                    {Object.entries(settings.notifications.push).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </h4>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={() => toggleNotification('push', key)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* SMS Notifications */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">SMS Notifications</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Receive important alerts via text message
                    </p>
                  </div>
                  <div className="px-6 py-4 space-y-4">
                    {Object.entries(settings.notifications.sms).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </h4>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={() => toggleNotification('sms', key)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Privacy Settings */}
            {activeSection === 'privacy' && (
              <div className="space-y-6">
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Privacy & Visibility</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Control how your information is shared and displayed
                    </p>
                  </div>
                  <div className="px-6 py-4 space-y-6">
                    {/* Profile Visibility */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Profile Visibility</label>
                      <select
                        value={settings.privacy.profileVisibility}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          privacy: { ...prev.privacy, profileVisibility: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="public">Public - Everyone can see your profile</option>
                        <option value="team">Team - Only team members can see your profile</option>
                        <option value="private">Private - Only you can see your profile</option>
                      </select>
                    </div>

                    {/* Privacy Toggles */}
                    {Object.entries(settings.privacy).filter(([key]) => key !== 'profileVisibility').map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </h4>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={() => togglePrivacySetting(key)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Application Settings */}
            {activeSection === 'application' && (
              <div className="space-y-6">
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Application Preferences</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Customize your application experience
                    </p>
                  </div>
                  <div className="px-6 py-4 space-y-6">
                    {/* Theme */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
                      <select
                        value={settings.application.theme}
                        onChange={(e) => setSettings(prev => ({
                          ...prev,
                          application: { ...prev.application, theme: e.target.value }
                        }))}
                        className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                        <option value="auto">Auto (System)</option>
                      </select>
                    </div>

                    {/* Other Application Settings */}
                    {Object.entries(settings.application).filter(([key]) => !['theme', 'defaultDashboard', 'itemsPerPage'].includes(key)).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between">
                        <div>
                          <h4 className="text-sm font-medium text-gray-900 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').toLowerCase()}
                          </h4>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={value}
                            onChange={() => toggleApplicationSetting(key)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Integrations */}
            {activeSection === 'integrations' && (
              <div className="space-y-6">
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">External Integrations</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Connect your external accounts and services
                    </p>
                  </div>
                  <div className="px-6 py-4 space-y-4">
                    {Object.entries(settings.integrations).map(([service, config]) => (
                      <div key={service} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <div className="p-2 bg-gray-100 rounded-lg mr-4">
                            <GlobeAltIcon className="h-5 w-5" />
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-gray-900 capitalize">{service}</h4>
                            <p className="text-sm text-gray-500">
                              {config.syncEnabled ? 'Connected' : 'Not connected'} 
                              {config.lastSync && ` • Last sync: ${new Date(config.lastSync).toLocaleString()}`}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={config.syncEnabled}
                              onChange={() => setSettings(prev => ({
                                ...prev,
                                integrations: {
                                  ...prev.integrations,
                                  [service]: {
                                    ...prev.integrations[service],
                                    syncEnabled: !prev.integrations[service].syncEnabled
                                  }
                                }
                              }))}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                          <button className="p-2 text-gray-400 hover:text-gray-600">
                            <Cog6ToothIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Billing */}
            {activeSection === 'billing' && (
              <div className="space-y-6">
                {/* Subscription Info */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Subscription & Billing</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Manage your subscription and payment information
                    </p>
                  </div>
                  <div className="px-6 py-4">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">{settings.billing.plan} Plan</h4>
                        <p className="text-sm text-gray-500">
                          Status: <span className="capitalize text-green-600">{settings.billing.status}</span>
                        </p>
                        <p className="text-sm text-gray-500">
                          Renews on {new Date(settings.billing.renewalDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-gray-900">$299.99</p>
                        <p className="text-sm text-gray-500">per month</p>
                      </div>
                    </div>

                    {/* Payment Method */}
                    <div className="border-t pt-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-4">Payment Method</h4>
                      <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                        <div className="flex items-center">
                          <CreditCardIcon className="h-8 w-8 text-gray-400 mr-3" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {settings.billing.paymentMethod.brand} ending in {settings.billing.paymentMethod.last4}
                            </p>
                            <p className="text-sm text-gray-500">
                              Expires {settings.billing.paymentMethod.expiryMonth}/{settings.billing.paymentMethod.expiryYear}
                            </p>
                          </div>
                        </div>
                        <button className="text-sm text-blue-600 hover:text-blue-800">
                          Update
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Billing History */}
                <div className="bg-white shadow rounded-lg">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Billing History</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Download invoices and view payment history
                    </p>
                  </div>
                  <div className="px-6 py-4">
                    <div className="space-y-4">
                      {settings.billing.billingHistory.map((invoice) => (
                        <div key={invoice.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{invoice.description}</p>
                            <p className="text-sm text-gray-500">
                              {new Date(invoice.date).toLocaleDateString()} • ${invoice.amount}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              invoice.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {invoice.status}
                            </span>
                            <button className="text-sm text-blue-600 hover:text-blue-800">
                              Download
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Danger Zone */}
        <div className="mt-12 bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start">
            <ExclamationTriangleIcon className="h-6 w-6 text-red-400 mr-3 mt-1" />
            <div className="flex-1">
              <h3 className="text-lg font-medium text-red-800">Danger Zone</h3>
              <p className="mt-2 text-sm text-red-700">
                These actions are irreversible. Please proceed with caution.
              </p>
              <div className="mt-4 flex items-center space-x-4">
                <button
                  onClick={handleDeleteAccount}
                  className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  Delete Account
                </button>
                <button className="inline-flex items-center px-4 py-2 border border-red-300 shadow-sm text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50">
                  <ArrowLeftOnRectangleIcon className="h-4 w-4 mr-2" />
                  Sign Out All Devices
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals would go here */}
      {/* You can implement password change, 2FA setup, and delete confirmation modals */}
      
      <Footer />
    </div>
  );
};

export default AccountSettings;
